import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import AdminConfirmDialog from "./AdminConfirmDialog";
import BulkActionBar from "./BulkActionBar";
import { Search, Star, EyeOff, Eye } from "lucide-react";

const STATUSES = [
    { id: "all", label: "All" },
    { id: "visible", label: "Visible" },
    { id: "hidden", label: "Hidden" },
    { id: "featured", label: "Featured" },
];

export default function MarketplaceTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/marketplace/listings", {
                params: { q: q.trim(), status, page, page_size: pageSize },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, status, page]);

    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    useEffect(() => {
        setPage(1);
    }, [q, status]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((i) => i.id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const runBulk = async (action, reason) => {
        try {
            const { data } = await api.post(
                `/admin/marketplace/listings/bulk?action=${action}`,
                { ids: selected, reason }
            );
            toast.success(`${data.affected} listings updated`);
            setConfirm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((i) => selected.includes(i.id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "title", label: "Title" },
                { key: "price", label: "Price" },
                { key: "currency", label: "Currency" },
                { key: "category", label: "Category" },
                { key: "condition", label: "Condition" },
                { key: "seller_id", label: "Seller ID" },
                { key: "featured", label: "Featured", map: (r) => (r.featured ? "yes" : "") },
                { key: "hidden_by_admin", label: "Hidden", map: (r) => (r.hidden_by_admin ? "yes" : "") },
                { key: "created_at", label: "Created" },
            ],
            "marketplace-listings"
        );
    };

    return (
        <div className="space-y-4">
            <div className="bg-snowcap border border-ridge-line rounded-lg p-4 space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                    <input
                        data-testid="marketplace-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search listings by title, ID, or seller…"
                        className="w-full pl-10 pr-4 py-2.5 rounded-md bg-alpine-mist border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 text-sm"
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                        <button
                            key={s.id}
                            data-testid={`marketplace-status-${s.id}`}
                            onClick={() => setStatus(s.id)}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition-all ${
                                status === s.id ? "bg-midnight-pine text-white" : "bg-alpine-mist text-slate-gray hover:text-granite"
                            }`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="marketplace-bulk"
                    actions={[
                        { id: "hide", label: "Hide", danger: true, onClick: () => setConfirm({ action: "hide", danger: true, title: `Hide ${selected.length} listing(s)?`, requireReason: true }) },
                        { id: "restore", label: "Restore", onClick: () => setConfirm({ action: "restore", title: `Restore ${selected.length} listing(s)?`, requireReason: false }) },
                        { id: "feature", label: "Feature", onClick: () => setConfirm({ action: "feature", title: `Feature ${selected.length} listing(s)?`, requireReason: false }) },
                        { id: "unfeature", label: "Unfeature", onClick: () => setConfirm({ action: "unfeature", title: `Unfeature ${selected.length} listing(s)?`, requireReason: false }) },
                        { id: "delete", label: "Delete", danger: true, onClick: () => setConfirm({ action: "delete", danger: true, title: `Delete ${selected.length} listing(s)?`, requireReason: true, description: "Irreversible." }) },
                    ]}
                />
            )}

            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-alpine-mist">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                <th className="py-3 pl-4 w-10">
                                    <input
                                        type="checkbox"
                                        data-testid="marketplace-select-all-cb"
                                        checked={items.length > 0 && selected.length === items.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className="py-3 px-4">Title</th>
                                <th className="py-3 px-4">Price</th>
                                <th className="py-3 px-4">Seller</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="py-10 px-4 text-center text-slate-gray">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="py-10 text-center text-slate-gray">No listings match.</td></tr>
                            ) : (
                                items.map((l) => (
                                    <tr
                                        key={l.id}
                                        data-testid={`marketplace-row-${l.id}`}
                                        className={`border-t border-ridge-line ${l.hidden_by_admin ? "bg-ember-orange/5" : ""}`}
                                    >
                                        <td className="py-3 pl-4">
                                            <input
                                                type="checkbox"
                                                data-testid={`marketplace-cb-${l.id}`}
                                                checked={selected.includes(l.id)}
                                                onChange={() => toggleOne(l.id)}
                                            />
                                        </td>
                                        <td className="py-3 px-4 font-semibold text-granite">
                                            <Link to={`/marketplace/${l.id}`} className="hover:text-ember-orange inline-flex items-center gap-1.5">
                                                {l.featured && <Star className="w-3.5 h-3.5 text-ember-orange fill-ember-orange" />}
                                                {l.title || "(untitled)"}
                                            </Link>
                                        </td>
                                        <td className="py-3 px-4 text-granite font-semibold">
                                            {l.currency || "USD"} {l.price}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-gray">
                                            {l.seller?.name || l.seller_id}
                                        </td>
                                        <td className="py-3 px-4">
                                            {l.hidden_by_admin ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-ember-orange/20 text-ember-dark">
                                                    <EyeOff className="w-3 h-3" /> Hidden
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-deep-evergreen/15 text-deep-evergreen">
                                                    <Eye className="w-3 h-3" /> Visible
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-gray">
                                            {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between p-4 border-t border-ridge-line">
                    <span className="text-xs text-slate-gray font-semibold">
                        Showing {items.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + items.length} of {total}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="marketplace-prev"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                        >
                            Prev
                        </button>
                        <span className="text-xs font-bold text-granite">{page} / {totalPages}</span>
                        <button
                            data-testid="marketplace-next"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            <AdminConfirmDialog
                open={!!confirm}
                onClose={() => setConfirm(null)}
                onConfirm={(reason) => runBulk(confirm.action, reason)}
                title={confirm?.title || ""}
                description={confirm?.description || "Logged to the audit trail."}
                confirmLabel="Apply"
                danger={confirm?.danger}
                requireReason={confirm?.requireReason}
            />
        </div>
    );
}
